/**
 * CORE-M-09 evidence-row primitive (G-8 contract).
 *
 * Chans's 2026-09-15 rebuild spec G-8:
 *   Every recommendation row carries its own evidence. Spend, the exact
 *   time window, conversions, and one plain sentence on why Core is
 *   raising it. "Spent and converted nothing" with no number, no window,
 *   and no reasoning is not actionable.
 *
 * Plan doc §CORE-M-09 acceptance: missing any field is a TEST FAILURE,
 * not a rendering fallback. TypeScript enforces the four fields as
 * required non-empty strings at the call site — a caller cannot render
 * the primitive without carrying all four. Runtime empty-string checks
 * throw in tests and warn in production so a serializer bug that empties
 * one field cannot silently reduce the row to three chips.
 *
 * Where the primitive belongs
 *
 * Any Management row that describes something Core is proposing based on
 * data pulled from Google or from Core's own compute. Concretely: the
 * per-row body of M-03, M-04, M-05, M-06, M-07, M-08 once each tab has
 * the four fields available in its response.
 *
 * Google-recommendations rows (M-02) are a documented exception: those
 * rows describe Google's own signal, not Core's compute, and Google's
 * recommendation payload does not include per-rec spend / conversions.
 * That is why M-02 renders its own chip layout instead of using this
 * primitive.
 *
 * Adoption register lives in ADOPTED_BY below so a reader can see at
 * a glance which tabs have moved onto the contract and which are still
 * on the phase-B backlog.
 */
import { EvidenceChip } from './EvidenceChip';
import { cn } from '@/lib/utils';

/**
 * The G-8 evidence contract. All four fields required and non-empty.
 * TypeScript enforces presence at the call site; the runtime check below
 * catches serializer/formatter bugs that would slip past compile-time.
 */
export interface RowEvidence {
  /** Formatted currency string. Example: "$0", "$1,247". Never empty. */
  spend: string;
  /** The exact window Core measured. Example: "last 30 days". Never empty. */
  window: string;
  /** Formatted conversion count. Example: "0 conv.", "3.4 conv.". Never empty. */
  conversions: string;
  /** One plain sentence on why Core is raising this row. Never empty. */
  reason: string;
}

/**
 * The G-8 contract field names, in Chans's spec order. Named as a
 * frozen array so tests can assert the runtime object's keys line up
 * with the spec. A rename of any of these four fields (spend -> amount,
 * window -> period, etc.) trips the contract-shape test in the
 * accompanying suite.
 *
 * A `satisfies` guard below makes sure this array cannot drift from
 * the RowEvidence keys at compile time.
 */
export const G8_CONTRACT_FIELDS = [
  'spend',
  'window',
  'conversions',
  'reason',
] as const satisfies ReadonlyArray<keyof RowEvidence>;

/**
 * A section (M-03 negatives, M-04..M-08 management rows) is REQUIRED
 * to appear in the register — Chans's plan doc §CORE-M-09 acceptance
 * says the contract test enforces this. M-02 Google Recommendations
 * is the documented exception; see the module docstring.
 */
export const REQUIRED_REGISTER_SECTIONS: readonly string[] = [
  'Negative Keyword Suggestions',
  'Campaign Review',
  'Ad Group Review',
  'Ad Review',
  'Keyword Review',
  'Budget Reallocation',
];

interface EvidenceRowProps {
  evidence: RowEvidence;
  className?: string;
  /**
   * Extra chips a specific tab wants alongside the four G-8 chips.
   * Rendered after the three metric chips, before the reason sentence.
   * The four G-8 fields are still required — extras cannot substitute.
   */
  additionalChips?: Array<{
    label: string;
    tone?: 'neutral' | 'positive' | 'warning' | 'danger';
    title?: string;
  }>;
}

/**
 * Adoption register. Two granularities per tab:
 *
 *   - `tabContext` — the tab-level summary strip that names WHY the tab
 *     has open items, using the same four G-8 fields. Cheap first
 *     adoption per tab; if the fields are missing here the tab body is
 *     the defect site.
 *   - `perRow` — each row body inside the tab renders EvidenceRow. This
 *     is the deep migration and typically ships in the tab's phase-B
 *     follow-up (M-03.c, M-06.b, M-08.b).
 *
 * Add an entry here in the same PR that wires the primitive into a tab,
 * so a reader can trace adoption from one place. Bus-factor: prefer
 * editing this list to writing a doc.
 *
 * Not exhaustive of every Management row — Google-recommendations rows
 * (M-02) intentionally do not adopt the primitive; see the module
 * docstring for the reasoning.
 */
export interface EvidenceRowAdoption {
  tab: string;
  /** The ticket that owns this migration. Named on the entry so the
   *  register is greppable back to the plan doc. */
  ticket: string;
  /** Adoption of the per-tab context strip. */
  tabContext: 'adopted' | 'pending';
  /** Adoption of the per-row body inside the tab. */
  perRow: 'adopted' | 'pending';
  note?: string;
}

export const EVIDENCE_ROW_ADOPTED_BY: readonly EvidenceRowAdoption[] = [
  {
    tab: 'Negative Keyword Suggestions',
    ticket: 'CORE-M-03.c',
    tabContext: 'adopted',
    perRow: 'adopted',
    note:
      'M-03 wrapper renders EvidenceRow on every row (CORE-M-03.c, 2026-09-18). The standalone /ads-tools/search-terms route still runs its own row shape until phase D retires it.',
  },
  {
    tab: 'Campaign Review',
    ticket: 'CORE-M-04',
    tabContext: 'pending',
    perRow: 'adopted',
    note: 'Every flagged row renders EvidenceRow (30-day spend, window, qualified conversions, reason) plus a 30/90-day table (CORE-M-04, 2026-09-24). No tab-level strip: the header states the counts instead.',
  },
  {
    tab: 'Ad Group Review',
    ticket: 'CORE-M-05',
    tabContext: 'pending',
    perRow: 'pending',
    note: 'Shell today (blocked on Q-6). Adoption lands with the row list.',
  },
  {
    tab: 'Ad Review',
    ticket: 'CORE-M-06.b',
    tabContext: 'adopted',
    perRow: 'pending',
    note:
      'Tab-level context strip renders EvidenceRow (CORE-M-09). Interior AdHealthTable per-row migration lands with M-06.b.',
  },
  {
    tab: 'Keyword Review',
    ticket: 'CORE-M-07',
    tabContext: 'pending',
    perRow: 'pending',
    note: 'Shell today (blocked on Q-3). Adoption lands with the row list.',
  },
  {
    tab: 'Budget Reallocation',
    ticket: 'CORE-M-08.b',
    tabContext: 'pending',
    perRow: 'pending',
    note:
      'Interior BudgetReallocationTab has its own row shape; migration lands with M-08.b.',
  },
] as const;

function assertNonEmpty(evidence: RowEvidence): void {
  const empty = (Object.keys(evidence) as (keyof RowEvidence)[]).filter(
    (k) => !evidence[k] || !evidence[k].trim(),
  );
  if (empty.length === 0) return;
  const message =
    `EvidenceRow received empty G-8 field(s): ${empty.join(', ')}. ` +
    'The G-8 contract requires spend, window, conversions and reason — ' +
    'missing any field is a defect (a serializer bug or a formatter that ' +
    'returned an empty string), not a rendering fallback.';
  if (process.env.NODE_ENV === 'test') {
    throw new Error(message);
  }
  // In production the row still renders (the operator sees something), but
  // the console names the defect so it does not go unnoticed.
  // eslint-disable-next-line no-console
  console.error(`[EvidenceRow] ${message}`);
}

export function EvidenceRow({ evidence, additionalChips, className }: EvidenceRowProps) {
  assertNonEmpty(evidence);
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-1.5">
        <EvidenceChip label={evidence.spend} title="Spend in this window" />
        <EvidenceChip label={evidence.window} title="Time window Core measured" />
        <EvidenceChip label={evidence.conversions} title="Conversions in this window" />
        {additionalChips?.map((chip, i) => (
          <EvidenceChip
            key={`extra-${i}`}
            label={chip.label}
            tone={chip.tone ?? 'neutral'}
            title={chip.title}
          />
        ))}
      </div>
      <p className="text-sm text-muted-foreground">{evidence.reason}</p>
    </div>
  );
}

export default EvidenceRow;
